import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { CreditCard as CreditCardIcon, Loader2, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import accountService from '@services/accountService';
import categoryService from '@services/categoryService';
import creditCardService from '@services/creditCardService';
import { CARD_BRAND_LABELS, creditCardToInput, type Account, type Category, type CreditCard } from '@/types/finance';
import { colorClasses } from '@/utils/colors';
import { formatDateBR } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { CardDrawer } from './CardDrawer';
import { CardFormModal } from './CardFormModal';
import { AmountText, InvoiceStatusBadge } from './financeBits';

/**
 * Finanças → Cartões: cartões de crédito com limite, "em aberto" (faturas não pagas) e a fatura
 * atual (situação, total, vencimento); cadastro/edição, inativar/reativar e o painel do cartão
 * com faturas e itens (`?open=<id>`).
 */
export const CardsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<CreditCard[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ card?: CreditCard } | null>(null);
  const [deactivating, setDeactivating] = useState<CreditCard | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const openId = Number(searchParams.get('open')) > 0 ? Number(searchParams.get('open')) : null;
  const openInvoiceId = Number(searchParams.get('invoice')) > 0 ? Number(searchParams.get('invoice')) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await creditCardService.getAll(showInactive));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    accountService.getAll().then(setAccounts).catch(setError);
    categoryService.getAll().then(setCategories).catch(setError);
  }, []);

  const open = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('open', String(id));
    else next.delete('open');
    next.delete('invoice');
    setSearchParams(next, { replace: true });
  };

  const active = useMemo(() => items.filter((c) => c.is_active !== false), [items]);
  const totals = useMemo(
    () => ({
      open: active.reduce((s, c) => s + Math.abs(c.open_total_cents), 0),
      limit: active.reduce((s, c) => s + (c.limit_cents ?? 0), 0),
    }),
    [active],
  );

  const confirmDeactivate = async () => {
    if (!deactivating) return;
    setBusy(deactivating.id);
    try {
      await creditCardService.deactivate(deactivating.id);
      setDeactivating(null);
      load();
    } catch (err) {
      setDeactivating(null);
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const reactivate = async (c: CreditCard) => {
    setBusy(c.id);
    try {
      await creditCardService.update(c.id, creditCardToInput(c, { is_active: true }));
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <CreditCardIcon size={20} className="text-slate-500" /> Cartões
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Mostrar inativos
          </label>
          <PermissionGuard permission="financas.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})}>
              Novo cartão
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Total geral */}
      <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Em aberto (todos)</p>
          <p className="text-xl font-semibold">
            <AmountText cents={-totals.open} muted={totals.open === 0} />
          </p>
          <p className="text-small">faturas ainda não pagas</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Limite total</p>
          <p className="text-xl font-semibold text-slate-800">{totals.limit ? formatBRL(totals.limit) : '—'}</p>
          {totals.limit > 0 && <p className="text-small">disponível {formatBRL(Math.max(0, totals.limit - totals.open))}</p>}
        </div>
      </div>

      {loading && items.length === 0 && (
        <p className="text-center text-slate-400 py-6">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      )}
      {!loading && items.length === 0 && (
        <p className="text-center text-slate-500 py-8">Nenhum cartão cadastrado. Comece por "Novo cartão": informe o dia de fechamento, o vencimento e a conta que paga a fatura.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((c) => {
          const inactive = c.is_active === false;
          const inv = c.current_invoice;
          return (
            <div
              key={c.id}
              className={clsx('card border-l-4 p-4 space-y-3 cursor-pointer hover:shadow-card-hover transition-shadow', colorClasses(c.color).borderLeft, (loading || inactive) && 'opacity-70')}
              onClick={() => open(c.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{c.name}</p>
                  <p className="text-small truncate">
                    {[c.brand && CARD_BRAND_LABELS[c.brand], c.last4 && `final ${c.last4}`, `fecha ${c.closing_day} · vence ${c.due_day}`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {inactive && <span className="badge-neutral">Inativo</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Em aberto</p>
                  <p className="text-base font-semibold">
                    <AmountText cents={-Math.abs(c.open_total_cents)} muted={c.open_total_cents === 0} />
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Limite</p>
                  <p className="text-base font-semibold text-slate-800">{c.limit_cents ? formatBRL(c.limit_cents) : '—'}</p>
                </div>
              </div>
              <div className="flex items-end justify-between gap-2">
                {inv ? (
                  <div className="min-w-0 text-sm">
                    <p className="flex flex-wrap items-center gap-1.5">
                      <span className="text-slate-600">Fatura {inv.label}</span>
                      <InvoiceStatusBadge status={inv.status} />
                    </p>
                    <p className="text-small">
                      <AmountText cents={inv.total_cents} muted={inv.total_cents === 0} /> · vence {formatDateBR(inv.due_date)}
                    </p>
                  </div>
                ) : (
                  <p className="text-small">Sem fatura no mês.</p>
                )}
                {canWrite && (
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ card: c })} />
                    {inactive ? (
                      <ActionButton icon={<Power size={16} />} variant="view" tooltip="Reativar" disabled={busy === c.id} onClick={() => reactivate(c)} />
                    ) : (
                      <ActionButton icon={<PowerOff size={16} />} variant="delete" tooltip="Inativar" disabled={busy === c.id} onClick={() => setDeactivating(c)} />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <CardFormModal
          card={form.card}
          accounts={accounts}
          categories={categories}
          onClose={() => setForm(null)}
          onSaved={(c) => {
            setForm(null);
            load();
            if (!form.card) open(c.id);
          }}
          onError={setError}
        />
      )}

      {openId && <CardDrawer cardId={openId} initialInvoiceId={openInvoiceId} accounts={accounts} categories={categories} onClose={() => open(null)} onChanged={load} onError={setError} />}

      <ConfirmDialog
        isOpen={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={confirmDeactivate}
        title="Inativar cartão"
        message={`Inativar "${deactivating?.name ?? ''}"? Faturas e itens ficam guardados; o cartão some das listas e dos seletores até ser reativado.`}
        confirmText="Inativar"
        severity="warning"
        loading={busy !== null}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
